import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLogin } from '../../context/LoginProvider';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import Navbar from '../../components/Navbar';
import Select from 'react-select';
import { countries } from '../../utils/countries';

const Profile = () => {
  const { userData, setUserData, updateUserData } = useLogin();
  const [interests, setInterests] = useState([]);
  const [newInterest, setNewInterest] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [isUploading, setIsUploading] = useState(false);
  const [joinDate, setJoinDate] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('purple');
  const [chatHistory, setChatHistory] = useState([]);
  const [interestsSuccess, setInterestsSuccess] = useState(false);
  const navigate = useNavigate();

  // Editing state for demographics
  const [isEditingDemographics, setIsEditingDemographics] = useState(false);
  const [editedAge, setEditedAge] = useState('');
  const [editedCountry, setEditedCountry] = useState('');
  const [editedRelationshipType, setEditedRelationshipType] = useState('');
  const [demographicsSuccess, setDemographicsSuccess] = useState(false);

  // Define theme options
  const themeOptions = [
    { id: 'purple', name: 'Purple Dream', color: '#6616cd' },
    { id: 'dark', name: 'Dark Theme (Coming Soon)', color: '#121212' },
    { id: 'light', name: 'Light Theme (Coming Soon)', color: '#f5f5f5' },
    // { id: 'blue', name: 'Ocean Blue (Coming Soon)', color: '#1676cd' },
    // { id: 'green', name: 'Forest Green (Coming Soon)', color: '#16cd66' },
  ];

  useEffect(() => {
    if (userData.username && !interests.length) {
      // Only fetch if we don't have interests yet
      fetchUserProfile(userData.username);
    } else if (userData.interests && !interests.length) {
      // Set from userData if available and we haven't set interests yet
      setInterests(userData.interests);
    }

    // Set Purple Dream as default selected theme
    setSelectedTheme('purple');
    document.documentElement.setAttribute('data-theme', 'purple');
  }, [userData]);

  // Hide success message after 3 seconds
  useEffect(() => {
    if (interestsSuccess) {
      const timer = setTimeout(() => {
        setInterestsSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [interestsSuccess]);

  // Update the fetchUserProfile function to properly handle profile photos
  const fetchUserProfile = async (username) => {
    try {
      const response = await fetch(`https://soulmegle.com/api/auth/user-profile?username=${username}`, {
        method: 'GET',
        credentials: 'include'
      }); 
  
      if (response.ok) {
        const profileData = await response.json();
        setJoinDate(new Date(profileData.joinDate).toLocaleDateString());
        setChatHistory(profileData.chatHistory || []);
        setSelectedTheme(profileData.theme || 'purple');
        
        // Update all user data from server
        updateUserData({
          name: profileData.name,
          age: profileData.age,
          country: profileData.country,
          relationshipType: profileData.relationshipType,
          profilePhoto: profileData.profilePhoto || "",
          theme: profileData.theme || "purple",
          joinDate: profileData.joinDate || "",
          chatHistory: profileData.chatHistory || []
        });
        
        // Update interests from the database
        if (profileData.interests && profileData.interests.length > 0) {
          setInterests(profileData.interests);
          // Only update userData if interests have changed
          if (JSON.stringify(profileData.interests) !== JSON.stringify(userData.interests)) {
            updateUserData({ interests: profileData.interests });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleAddInterest = () => {
    if (newInterest.trim()) {
      // Check if interest already exists (case insensitive)
      const interestExists = interests.some(
        interest => interest.toLowerCase() === newInterest.trim().toLowerCase()
      );
      
      if (!interestExists) {
        const updatedInterests = [...interests, newInterest.trim()];
        setInterests(updatedInterests);
        setNewInterest('');
      } else {
        alert('This interest already exists in your list.');
      }
    }
  };

  // Add key press handler for better UX
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && newInterest.trim()) {
      handleAddInterest();
    }
  };

  const handleRemoveInterest = (index) => {
    setInterests(interests.filter((_, i) => i !== index));
  };

  const handleSaveInterests = async () => {
    try {
      const response = await fetch('https://soulmegle.com/api/auth/update-interests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          interests: interests,
          isLoggedIn: true
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Update userData with the new interests
        updateUserData({ interests: data.interests });
        setInterestsSuccess(true);
      } else {
        // Handle validation errors
        const errorData = await response.json();
        alert(errorData.error || "Failed to save interests. Please try again.");
      }
    } catch (error) {
      console.error('Error updating interests:', error);
      alert("Failed to save interests. Please try again.");
    }
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    // Limit file size to 2MB for better MongoDB performance
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      alert('File size must be less than 2MB');
      return;
    }

    // Create a base64 version of the image
    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64Image = reader.result;
        
        // Resize and compress the image before uploading
        const compressedImage = await compressImage(base64Image, 400); // 400px max width/height
        
        // First, show the image immediately in the UI for better user experience
        updateProfilePhotoUI(compressedImage);

        const response = await fetch('https://soulmegle.com/api/auth/update-profile-photo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: userData.username,
            profilePhoto: compressedImage
          }),
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          updateUserData({ profilePhoto: data.profilePhoto });
          alert('Profile photo updated successfully!');
        } else {
          alert('Failed to update profile photo. Please try again.');
        }
      } catch (error) {
        console.error('Error updating profile photo:', error);
        alert('Error updating profile photo: ' + error.message);
      } finally {
        setIsUploading(false);
      }
    };
    reader.onerror = () => {
      setIsUploading(false);
      alert('Error reading file');
    };
    reader.readAsDataURL(file);
  };

  // Helper function to update UI immediately
  const updateProfilePhotoUI = (imageData) => {
    const photoContainer = document.querySelector('.profile-photo-container');
    if (photoContainer) {
      if (photoContainer.querySelector('img')) {
        photoContainer.querySelector('img').src = imageData;
      } else {
        // Create new image if placeholder is showing
        const placeholder = photoContainer.querySelector('.profile-photo-placeholder');
        if (placeholder) {
          placeholder.style.display = 'none';
          const img = document.createElement('img');
          img.src = imageData;
          img.alt = 'Profile';
          img.className = 'profile-photo';
          photoContainer.prepend(img);
        }
      }
    }
  };

  // Helper function to compress and resize images
  const compressImage = (base64Image, maxDimension) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64Image;
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxDimension) {
          height = Math.round(height * (maxDimension / width));
          width = maxDimension;
        } else if (height > maxDimension) {
          width = Math.round(width * (maxDimension / height));
          height = maxDimension;
        }
        
        // Create canvas and resize image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get compressed image as base64 string with reduced quality
        const compressedImage = canvas.toDataURL('image/jpeg', 0.7); // 70% quality JPEG
        resolve(compressedImage);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
    });
  };

  const handleThemeChange = async (theme) => {
    if (theme !== 'purple') {
      alert("Theme is under development. Only Purple Dream theme is currently available.");
      return;
    }

    setSelectedTheme(theme);
    try {
        const response = await fetch('https://soulmegle.com/api/auth/update-theme', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          theme: theme
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        updateUserData({ theme: data.theme });
        // Apply theme change to the app
        document.documentElement.setAttribute('data-theme', theme);
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // Add a refresh button to the interests tab
  const handleRefreshInterests = async () => {
    if (userData.username) {
      await fetchUserProfile(userData.username);
    }
  };

  // Demographic editing functions
  const countryOptions = countries.map(country => ({
    value: country.name,
    label: country.name,
    code: country.code
  }));

  const relationshipOptions = [
    { value: "friendship", label: "Friendship" },
    { value: "dating", label: "Dating" },
    { value: "serious_relationship", label: "Serious Relationship" },
    { value: "casual", label: "Casual" },
    { value: "networking", label: "Networking" },
    { value: "any", label: "Open to Anything" }
  ];

  const handleEditDemographics = () => {
    setEditedAge(userData.age?.toString() || '');
    setEditedCountry(userData.country || '');
    setEditedRelationshipType(userData.relationshipType || '');
    setIsEditingDemographics(true);
  };

  const handleCancelEdit = () => {
    setIsEditingDemographics(false);
    setEditedAge('');
    setEditedCountry('');
    setEditedRelationshipType('');
  };

  const validateAge = (age) => {
    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < 13 || ageNum > 120) {
      return "Age must be between 13 and 120";
    }
    return "";
  };

  const handleSaveDemographics = async () => {
    // Validate inputs
    const ageError = validateAge(editedAge);
    if (ageError) {
      alert(ageError);
      return;
    }
    if (!editedCountry) {
      alert("Country is required");
      return;
    }
    if (!editedRelationshipType) {
      alert("Relationship type is required");
      return;
    }

    try {
      const response = await fetch('https://soulmegle.com/api/auth/update-demographics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          age: parseInt(editedAge),
          country: editedCountry,
          relationshipType: editedRelationshipType
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Update userData with the new demographics
        updateUserData({ 
          age: parseInt(editedAge), 
          country: editedCountry,
          relationshipType: editedRelationshipType
        });
        setIsEditingDemographics(false);
        setDemographicsSuccess(true);
        setTimeout(() => setDemographicsSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update demographics');
      }
    } catch (error) {
      console.error('Error updating demographics:', error);
      alert("Failed to save demographics. Please try again.");
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="tab-content general-tab">
            <div className="profile-photo-section">
              <label htmlFor="profile-photo-input" className="profile-photo-container">
                {userData.profilePhoto ? (
                  <img
                    src={userData.profilePhoto}
                    alt="Profile"
                    className="profile-photo"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML += `<div class="profile-photo-placeholder">
                        ${userData.name ? userData.name.charAt(0).toUpperCase() : '?'}
                      </div>`;
                    }}
                  />
                ) : (
                  <div className="profile-photo-placeholder">
                    {userData.name ? userData.name.charAt(0).toUpperCase() : '?'}
                  </div>
                )}
                <div className="change-photo-button">
                  {isUploading ? 'Uploading...' : 'Change Photo'}
                </div>
                <input
                  id="profile-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  style={{ display: 'none' }}
                />
              </label>
              <div className="photo-instructions">
                <p>Click on the photo to change your profile picture</p>
                <p>(Max size: 2MB, Formats: JPEG, PNG, GIF)</p>
              </div>
            </div>

            <div className="profile-info">
              <div className="info-header">
                <h3>Profile Information</h3>
                {!isEditingDemographics && (
                  <button 
                    className="edit-demographics-btn"
                    onClick={handleEditDemographics}
                  >
                    ✏️ Edit Demographics
                  </button>
                )}
              </div>

              {demographicsSuccess && (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: '10px 15px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    textAlign: 'center'
                  }}
                >
                  Demographics updated successfully!
                </motion.div>
              )}

              <div className="info-item">
                <span>Name:</span>
                <span>{userData.name}</span>
              </div>
              <div className="info-item">
                <span>Email:</span>
                <span>{userData.email}</span>
              </div>
              
              <div className="info-item">
                <span>Age:</span>
                {isEditingDemographics ? (
                  <input
                    type="number"
                    value={editedAge}
                    onChange={(e) => setEditedAge(e.target.value)}
                    min="13"
                    max="120"
                    className="edit-input"
                  />
                ) : (
                <span>{userData.age || 'Not specified'}</span>
                )}
              </div>

              <div className="info-item">
                <span>Country:</span>
                {isEditingDemographics ? (
                  <Select
                    value={countryOptions.find(option => option.value === editedCountry)}
                    onChange={(selectedOption) => setEditedCountry(selectedOption ? selectedOption.value : '')}
                    options={countryOptions}
                    placeholder="Select country"
                    isSearchable
                    className="edit-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '35px',
                        fontSize: '14px'
                      })
                    }}
                  />
                ) : (
                <span>{userData.country || 'Not specified'}</span>
                )}
              </div>

              <div className="info-item">
                <span>Looking For:</span>
                {isEditingDemographics ? (
                  <Select
                    value={relationshipOptions.find(option => option.value === editedRelationshipType)}
                    onChange={(selectedOption) => setEditedRelationshipType(selectedOption ? selectedOption.value : '')}
                    options={relationshipOptions}
                    placeholder="Select relationship type"
                    isSearchable={false}
                    className="edit-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '35px',
                        fontSize: '14px'
                      })
                    }}
                  />
                ) : (
                  <span>
                    {userData.relationshipType ? 
                      relationshipOptions.find(opt => opt.value === userData.relationshipType)?.label || userData.relationshipType
                      : 'Not specified'
                    }
                  </span>
                )}
              </div>

              <div className="info-item">
                <span>Joined:</span>
                <span>{joinDate}</span>
              </div>

              {isEditingDemographics && (
                <div className="edit-buttons">
                  <button 
                    className="save-btn"
                    onClick={handleSaveDemographics}
                  >
                    💾 Save Changes
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    ❌ Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case 'interests':
        return (
          <div className="tab-content interests-tab">
            <div className="interests-editor">
              <div className="interests-header">
                <h3>Your Interests</h3>
                <button 
                  className="refresh-button"
                  onClick={handleRefreshInterests}
                  title="Refresh interests from database"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                  </svg>
                </button>
              </div>
              
              <div className="add-interest">
                <input
                  type="text"
                  value={newInterest}
                  onChange={(e) => setNewInterest(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add new interest"
                  maxLength={30}
                />
                <button
                  onClick={handleAddInterest}
                  disabled={!newInterest.trim()}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  Add
                </button>
              </div>
              
              <div className="interests-count">
                {interests.length > 0 ? 
                  `Showing ${interests.length} interest${interests.length !== 1 ? 's' : ''}` : 
                  'No interests added yet'}
              </div>
              
              <div className="interests-list">
                {interests.map((interest, index) => (
                  <motion.div
                    key={index}
                    className="interest-tag editable"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    {interest}
                    <button onClick={() => handleRemoveInterest(index)}>×</button>
                  </motion.div>
                ))}
              </div>
              
              {interestsSuccess && (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: '10px 15px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    textAlign: 'center'
                  }}
                >
                  Interests saved successfully!
                </motion.div>
              )}
              <button
                className="save-button"
                onClick={handleSaveInterests}
              >
                Save Changes
              </button>
            </div>
          </div>
        );

      case 'theme':
        return (
          <div className="tab-content theme-tab">
            <h3>Choose Your Theme</h3>
            <div className="theme-options">
              {themeOptions.map((theme) => (
                <div
                  key={theme.id}
                  data-id={theme.id}
                  className={`theme-option ${selectedTheme === theme.id ? 'selected' : ''}`}
                  onClick={() => handleThemeChange(theme.id)}
                >
                  <div className="theme-color" style={{ backgroundColor: theme.color }}></div>
                  <div className="theme-name">{theme.name}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'history':
        return (
          <div className="tab-content history-tab">
            <h3>Chat History</h3>
            {chatHistory.length > 0 ? (
              <div className="chat-history-list">
                {chatHistory.map((chat, index) => (
                  <div key={index} className="chat-history-item">
                    <div className="chat-date">
                      {new Date(chat.timestamp).toLocaleString()}
                    </div>
                    <div className="chat-summary">{chat.summary}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="work-in-progress">
                <svg viewBox="0 0 24 24" width="48" height="48">
                  <path d="M13 2.05v3.03c3.39.49 6 3.39 6 6.92 0 .9-.18 1.75-.5 2.54l2.54 1.54c.56-1.24.88-2.62.88-4.08 0-5.52-4.14-10.04-9.42-10.95zM12 19c-3.87 0-7-3.13-7-7 0-3.53 2.61-6.43 6-6.92V2.05c-5.28.91-9.42 5.43-9.42 10.95 0 6.08 4.93 11 11 11 2.4 0 4.62-.73 6.46-1.95l-2.59-1.54C15.12 18.5 13.62 19 12 19z" />
                </svg>
                <p>Work in Progress</p>
                <p>Chat history feature is coming soon!</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Navbar />
      <motion.div
        className="profile-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >

        <div className="profile-header">
          <h1>Profile</h1>
          <p>Manage your profile, preferences, and account settings</p>
        </div>

        <div className="profile-tabs">
          <button
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`tab-button ${activeTab === 'interests' ? 'active' : ''}`}
            onClick={() => setActiveTab('interests')}
          >
            Interests
          </button>
          <button
            className={`tab-button ${activeTab === 'theme' ? 'active' : ''}`}
            onClick={() => setActiveTab('theme')}
          >
            Themes
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Chat History
          </button>
        </div>

        <div className="profile-content">
          {renderTabContent()}
        </div>
      </motion.div>
    </>
  );
};

export default Profile;